import Cookies from 'js-cookie';

export interface CartItem {
  job_position_id: string;
  quantity: number;
  seniority_level: number;
  english_level: string;
  amount?: number | null;
  contract_duration?: number | null;
}

export interface LocalCart {
  items: CartItem[];
  company_name?: string;
  contact_name?: string;
  email?: string;
  phone?: string;
  company_type?: string;
}

export interface LocalProjectDetails {
  projectDescription: string;
  teamInfo: string;
  requiredSoftware: string;
  jobDescriptionUrl: string;
  responsibilities: string;
  jobDescriptionFilePath?: string;
}

const CART_COOKIE_NAME = 'local_cart';
const PROJECT_DETAILS_COOKIE_NAME = 'local_project_details';

// Determine if we should use secure cookies (HTTPS in production)
// Only use secure in production with HTTPS, never in development
const isSecure = typeof window !== 'undefined' && 
  window.location.protocol === 'https:' && 
  process.env.NODE_ENV === 'production';

const COOKIE_OPTIONS = {
  expires: 7, // 7 days
  sameSite: 'lax' as const,
  secure: isSecure
};

// Debug cookie options
if (typeof window !== 'undefined') {
  console.log('[cookieCart] Cookie options:', {
    secure: isSecure,
    protocol: window.location.protocol,
    env: process.env.NODE_ENV
  });
}

export const cookieCart = {
  /**
   * Get the current cart from cookies
   */
  get(): LocalCart | null {
    const data = Cookies.get(CART_COOKIE_NAME);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      console.error('[cookieCart] Failed to parse cart data');
      return null;
    }
  },

  /**
   * Set the entire cart in cookies
   */
  set(cart: LocalCart): void {
    try {
      const cartData = JSON.stringify(cart);
      Cookies.set(CART_COOKIE_NAME, cartData, COOKIE_OPTIONS);
      
      // Verify the cookie was set
      const verify = Cookies.get(CART_COOKIE_NAME);
      if (!verify) {
        console.error('[cookieCart] Cookie was not set! This might be a cookie policy issue.');
        console.log('[cookieCart] Attempted to save:', cartData.substring(0, 100) + '...');
      } else {
        console.log('[cookieCart] Cookie verified successfully, length:', verify.length);
      }
    } catch (error) {
      console.error('[cookieCart] Failed to set cookie:', error);
      throw error;
    }
  },

  /**
   * Add an item to the cart (increments quantity if exists)
   */
  addItem(item: CartItem): void {
    console.log('[cookieCart] addItem called with:', item);
    
    if (!item || !item.job_position_id) {
      console.error('[cookieCart] Invalid item:', item);
      throw new Error('Invalid cart item');
    }
    
    const cart = this.get() || { items: [] };
    const existingIndex = cart.items.findIndex(
      i => i.job_position_id === item.job_position_id &&
           i.seniority_level === item.seniority_level &&
           i.english_level === item.english_level
    );
    
    if (existingIndex >= 0) {
      cart.items[existingIndex].quantity += item.quantity;
      console.log('[cookieCart] Updated existing item quantity:', cart.items[existingIndex].quantity);
    } else {
      cart.items.push(item);
      console.log('[cookieCart] Added new item to cart');
    }
    
    try {
      this.set(cart);
      console.log('[cookieCart] Item saved to cookie successfully:', { 
        item, 
        totalItems: cart.items.length,
        totalQuantity: cart.items.reduce((sum, i) => sum + i.quantity, 0)
      });
    } catch (error) {
      console.error('[cookieCart] Failed to save item to cookie:', error);
      throw error;
    }
  },

  /**
   * Remove an item from the cart
   */
  removeItem(job_position_id: string, seniority_level: number, english_level: string): void {
    const cart = this.get();
    if (!cart) return;
    
    cart.items = cart.items.filter(
      i => !(i.job_position_id === job_position_id &&
             i.seniority_level === seniority_level &&
             i.english_level === english_level)
    );
    
    this.set(cart);
    console.log('[cookieCart] Item removed:', { job_position_id, remainingItems: cart.items.length });
  },

  /**
   * Update quantity of an existing item
   */
  updateQuantity(job_position_id: string, seniority_level: number, english_level: string, quantity: number): void {
    const cart = this.get();
    if (!cart) return;
    
    const item = cart.items.find(
      i => i.job_position_id === job_position_id &&
           i.seniority_level === seniority_level &&
           i.english_level === english_level
    );
    
    if (item) {
      if (quantity <= 0) {
        this.removeItem(job_position_id, seniority_level, english_level);
      } else {
        item.quantity = quantity;
        this.set(cart);
        console.log('[cookieCart] Quantity updated:', { job_position_id, quantity });
      }
    }
  },

  /**
   * Update cart metadata (company info, etc)
   */
  updateMetadata(metadata: Partial<Omit<LocalCart, 'items'>>): void {
    const cart = this.get() || { items: [] };
    Object.assign(cart, metadata);
    this.set(cart);
    console.log('[cookieCart] Metadata updated:', metadata);
  },

  /**
   * Clear the entire cart
   */
  clear(): void {
    Cookies.remove(CART_COOKIE_NAME);
    console.log('[cookieCart] Cart cleared');
  },

  /**
   * Get total item count in cart
   */
  getItemCount(): number {
    const cart = this.get();
    return cart?.items.reduce((sum, item) => sum + item.quantity, 0) || 0;
  }
};

export const cookieProjectDetails = {
  /**
   * Get project details from cookies
   */
  get(): LocalProjectDetails | null {
    const data = Cookies.get(PROJECT_DETAILS_COOKIE_NAME);
    if (!data) return null;
    try {
      return JSON.parse(data);
    } catch {
      console.error('[cookieProjectDetails] Failed to parse project details data');
      return null;
    }
  },

  /**
   * Set project details in cookies
   */
  set(details: LocalProjectDetails): void {
    try {
      const detailsData = JSON.stringify(details);
      Cookies.set(PROJECT_DETAILS_COOKIE_NAME, detailsData, COOKIE_OPTIONS);
      console.log('[cookieProjectDetails] Project details saved to cookie');
    } catch (error) {
      console.error('[cookieProjectDetails] Failed to set cookie:', error);
      throw error;
    }
  },

  /**
   * Update project details (partial update)
   */
  update(details: Partial<LocalProjectDetails>): void {
    const current = this.get() || {
      projectDescription: '',
      teamInfo: '',
      requiredSoftware: '',
      jobDescriptionUrl: '',
      responsibilities: '',
      jobDescriptionFilePath: ''
    };
    const updated = { ...current, ...details };
    this.set(updated);
    console.log('[cookieProjectDetails] Project details updated');
  },

  /**
   * Clear project details from cookies
   */
  clear(): void {
    Cookies.remove(PROJECT_DETAILS_COOKIE_NAME);
    console.log('[cookieProjectDetails] Project details cleared');
  }
};

